import { Location } from '@angular/common';
import { ChangeDetectionStrategy, Component, ViewChild, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, UrlSegment } from '@angular/router';
import { TuiButton } from '@taiga-ui/core';
import { AccountComponent } from '@features/settings/pages/account/account';
import { CompanyComponent } from '@features/settings/pages/company/company';
import {
  UsersRolesComponent,
  UsersRolesTab,
} from '@features/settings/pages/users-roles/users-roles';
import { UserFormComponent } from '@features/settings/pages/users-roles/components/user-form/user-form';
import { RoleFormComponent } from '@features/settings/pages/users-roles/components/role-form/role-form';
import { SubscriptionsComponent } from '@features/settings/pages/subscriptions/subscriptions';
import { PlansComponent } from '@features/settings/pages/plans/plans';
import { BillingComponent } from '@features/settings/pages/billing/billing';
import { PrivacySecurityComponent } from '@features/settings/pages/privacy-security/privacy-security';
import { IntegrationsComponent } from '@features/settings/pages/integrations/integrations';
import { RegionalSettingsComponent } from '@features/settings/pages/regional-settings/regional-settings';
import { TagsComponent } from '@features/settings/pages/tags/tags';
import { ManageFilesComponent } from '@features/settings/pages/manage-files/manage-files';

export type SettingsSection =
  | 'account'
  | 'company'
  | 'users-roles'
  | 'subscriptions'
  | 'plans'
  | 'billing'
  | 'privacy-security'
  | 'integrations'
  | 'regional-settings'
  | 'tags'
  | 'manage-files';

interface SettingsNavItem {
  readonly key: SettingsSection;
  readonly label: string;
  readonly icon: string;
  readonly description: string;
  readonly actionLabel?: string;
  readonly actionIcon?: string;
}

type SettingsView = 'list' | 'user-form' | 'role-form';

interface SettingsRouteState {
  readonly section: SettingsSection;
  readonly usersRolesTab: UsersRolesTab;
  readonly view: SettingsView;
  readonly userId: number | null;
  readonly roleId: number | null;
  readonly copyRole: boolean;
}

const SETTINGS_NAV: readonly SettingsNavItem[] = [
  {
    key: 'account',
    label: 'Account',
    icon: '@tui.user',
    description: 'Manage your profile, contact details, and password.',
  },
  {
    key: 'company',
    label: 'My company',
    icon: '@tui.briefcase',
    description: 'Update organization details, billing contacts, and branding.',
  },
  {
    key: 'users-roles',
    label: 'Users and roles',
    icon: '@tui.users',
    description: 'Control team access, roles, and permissions.',
  },
  {
    key: 'plans',
    label: 'Plans & Pricing',
    icon: '@tui.layout-grid',
    description: 'Compare available plans and pricing by product group.',
  },
  {
    key: 'subscriptions',
    label: 'Subscriptions',
    icon: '@tui.circle-dollar-sign',
    description: 'Review active plans, usage, and included features.',
  },
  {
    key: 'billing',
    label: 'Billing',
    icon: '@tui.credit-card',
    description: 'View invoices, payment methods, and billing status.',
  },
  {
    key: 'privacy-security',
    label: 'Privacy and Security',
    icon: '@tui.shield',
    description: 'Manage sessions, registered devices, and privacy documents.',
  },
  {
    key: 'integrations',
    label: 'Integrations',
    icon: '@tui.code-xml',
    description: 'Configure third-party connections and external API access.',
  },
  {
    key: 'regional-settings',
    label: 'Regional settings',
    icon: '@tui.globe',
    description: 'Choose the default language for PDF reports.',
  },
  {
    key: 'tags',
    label: 'Tags',
    icon: '@tui.tag',
    description: 'Create and manage reusable tags across your workspace.',
    actionLabel: 'Create tag',
    actionIcon: '@tui.plus',
  },
  {
    key: 'manage-files',
    label: 'Files',
    icon: '@tui.folder',
    description: 'Manage uploaded files.',
  },
] as const;

@Component({
  selector: 'app-settings-modal',
  templateUrl: 'modal.html',
  styleUrl: 'modal.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TuiButton,
    AccountComponent,
    CompanyComponent,
    UsersRolesComponent,
    UserFormComponent,
    RoleFormComponent,
    SubscriptionsComponent,
    PlansComponent,
    BillingComponent,
    PrivacySecurityComponent,
    IntegrationsComponent,
    RegionalSettingsComponent,
    TagsComponent,
    ManageFilesComponent,
  ],
})
export class ModalComponent {
  @ViewChild(TagsComponent) private tagsComponent?: TagsComponent;

  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly initialState = this.parsePathSegments(this.route.snapshot.url);

  protected readonly navItems = SETTINGS_NAV;
  protected readonly activeSection = signal<SettingsSection>(this.initialState.section);
  protected readonly usersRolesTab = signal<UsersRolesTab>(this.initialState.usersRolesTab);
  protected readonly activeView = signal<SettingsView>(this.initialState.view);
  protected readonly editingUserId = signal<number | null>(this.initialState.userId);
  protected readonly editingRoleId = signal<number | null>(this.initialState.roleId);
  protected readonly copyingRole = signal(this.initialState.copyRole);

  protected readonly activeItem = computed(
    () => this.navItems.find((item) => item.key === this.activeSection()) ?? this.navItems[0]!,
  );
  protected readonly isUsersRolesList = computed(
    () => this.activeSection() === 'users-roles' && this.activeView() === 'list',
  );
  protected readonly isUserFormView = computed(
    () => this.activeSection() === 'users-roles' && this.activeView() === 'user-form',
  );
  protected readonly isRoleFormView = computed(
    () => this.activeSection() === 'users-roles' && this.activeView() === 'role-form',
  );
  protected readonly isSubpage = computed(
    () => this.isUserFormView() || this.isRoleFormView(),
  );
  protected readonly headerTitle = computed(() => {
    if (this.isUserFormView()) {
      return this.editingUserId() === null ? 'Add user' : 'Edit user';
    }

    if (this.isRoleFormView()) {
      if (this.copyingRole()) {
        return 'Duplicate role';
      }

      return this.editingRoleId() === null ? 'Add role' : 'Edit role';
    }

    return this.activeItem().label;
  });
  protected readonly headerDescription = computed(() => {
    if (this.isUserFormView()) {
      return this.editingUserId() === null
        ? 'Create a team member profile and assign the right access role.'
        : 'Update profile details, access level, and login settings for this user.';
    }

    if (this.isRoleFormView()) {
      return this.copyingRole()
        ? 'Create a new role from an existing permission set.'
        : this.editingRoleId() === null
          ? 'Create a role and define the permissions it grants.'
          : 'Update the permissions and access rules assigned to this role.';
    }

    return this.activeItem().description;
  });

  protected navigateTo(section: SettingsSection): void {
    this.activeSection.set(section);
    this.activeView.set('list');
    this.editingUserId.set(null);
    this.editingRoleId.set(null);
    this.copyingRole.set(false);

    if (section === 'users-roles') {
      this.updateUsersRolesListUrl(this.usersRolesTab());
      return;
    }

    this.updatePath(['settings', section]);
  }

  protected changeUsersRolesTab(tab: UsersRolesTab): void {
    this.usersRolesTab.set(tab);
    this.activeSection.set('users-roles');
    this.activeView.set('list');
    this.editingUserId.set(null);
    this.editingRoleId.set(null);
    this.copyingRole.set(false);
    this.updateUsersRolesListUrl(tab);
  }

  protected openUserForm(userId: number | null): void {
    this.activeSection.set('users-roles');
    this.usersRolesTab.set('users');
    this.activeView.set('user-form');
    this.editingUserId.set(userId);
    this.editingRoleId.set(null);
    this.copyingRole.set(false);
    this.updatePath(
      userId === null
        ? ['settings', 'users-roles', 'users', 'create']
        : ['settings', 'users-roles', 'users', String(userId), 'edit'],
    );
  }

  protected openRoleForm(roleId: number | null, copy = false): void {
    this.activeSection.set('users-roles');
    this.usersRolesTab.set('roles');
    this.activeView.set('role-form');
    this.editingUserId.set(null);
    this.editingRoleId.set(roleId);
    this.copyingRole.set(copy);

    this.updatePath(
      roleId === null
        ? ['settings', 'users-roles', 'roles', 'create']
        : ['settings', 'users-roles', 'roles', String(roleId), copy ? 'copy' : 'edit'],
    );
  }

  protected backToUsersRolesList(): void {
    this.activeSection.set('users-roles');
    this.activeView.set('list');
    this.editingUserId.set(null);
    this.editingRoleId.set(null);
    this.copyingRole.set(false);
    this.updateUsersRolesListUrl(this.usersRolesTab());
  }

  protected runHeaderAction(): void {
    switch (this.activeSection()) {
      case 'tags':
        this.tagsComponent?.startAddFromHeader();
        break;
    }
  }

  private updateUsersRolesListUrl(tab: UsersRolesTab): void {
    this.updatePath(['settings', 'users-roles', tab]);
  }

  private updatePath(segments: ReadonlyArray<string>): void {
    const url = this.router
      .createUrlTree(
        [
          {
            outlets: {
              modal: segments,
            },
          },
        ],
        {
          queryParamsHandling: 'preserve',
        },
      )
      .toString();

    this.location.go(url);
  }

  private parsePathSegments(segments: readonly UrlSegment[]): SettingsRouteState {
    const paths = segments.map((segment) => segment.path);
    const settingsIndex = paths.indexOf('settings');
    const nested = settingsIndex >= 0 ? paths.slice(settingsIndex + 1) : paths;
    const section = this.coerceSection(nested[0]);

    if (section !== 'users-roles') {
      return {
        section,
        usersRolesTab: 'users',
        view: 'list',
        userId: null,
        roleId: null,
        copyRole: false,
      };
    }

    const tab = this.coerceUsersRolesTab(nested[1]);
    const entityId = this.coerceEntityId(nested[2]);
    const action = nested[entityId === null ? 2 : 3];

    if (tab === 'users') {
      return {
        section,
        usersRolesTab: tab,
        view: action === 'create' || action === 'edit' ? 'user-form' : 'list',
        userId: action === 'edit' ? entityId : null,
        roleId: null,
        copyRole: false,
      };
    }

    return {
      section,
      usersRolesTab: tab,
      view: action === 'create' || action === 'edit' || action === 'copy' ? 'role-form' : 'list',
      userId: null,
      roleId: action === 'edit' || action === 'copy' ? entityId : null,
      copyRole: action === 'copy',
    };
  }

  private coerceSection(value: string | undefined): SettingsSection {
    if (SETTINGS_NAV.some((item) => item.key === value)) {
      return value as SettingsSection;
    }

    return 'account';
  }

  private coerceUsersRolesTab(value: string | undefined): UsersRolesTab {
    return value === 'roles' ? 'roles' : 'users';
  }

  private coerceEntityId(value: string | undefined): number | null {
    if (!value) return null;
    const id = Number(value);
    return Number.isInteger(id) && id > 0 ? id : null;
  }
}
